'use client'

import {
  ComponentPropsWithoutRef,
  ElementRef,
  forwardRef,
  HTMLAttributes,
} from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'

import { mtc } from '../../../utils/style-helpers'
import { Button } from '../../atoms/button'
import {
  closeStyles,
  contentStyles,
  dismissStyles,
  overlayStyles,
  footerStyles,
  descriptionStyles,
  titleStyles,
} from './styles'

export enum DialogColors {
  LEVEL_0 = 'level0',
  LEVEL_1 = 'level1',
  PRIORITY = 'priority',
  EMPHASIS = 'emphasis',
  BRAND = 'brand',
  SUCCESS = 'success',
  WARNING = 'warning',
  DANGER = 'danger',
}

type DialogProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Root> & {
  onClose?: () => void
}

const DialogRoot = ({ onClose, onOpenChange, ...rest }: DialogProps) => {
  const handleOnClose = (open: boolean) => {
    onOpenChange?.(open)

    if (!open) {
      onClose?.()
    }
  }

  return <DialogPrimitive.Root {...rest} onOpenChange={handleOnClose} />
}

export const DialogTrigger = DialogPrimitive.Trigger
export const DialogPortal = DialogPrimitive.Portal
export const DialogClose = DialogPrimitive.Close

export const DialogOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={mtc(overlayStyles(), className)}
    {...props}
  />
))

export const DialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    dismissClassName?: string
    onDismiss?: () => void
    size?: 'sm' | 'md' | 'lg'
    heading?: string
    description?: string
  }
>(
  (
    {
      className,
      children,
      dismissClassName,
      onDismiss,
      size,
      heading,
      description,
      ...props
    },
    ref
  ) => (
    <DialogPortal>
      <DialogOverlay />

      <DialogPrimitive.Content
        ref={ref}
        className={mtc(contentStyles({ size }), className)}
        aria-describedby={description ? 'dialog-description' : undefined}
        {...props}
      >
        {heading && <DialogTitle>{heading}</DialogTitle>}
        {description && <DialogDescription>{description}</DialogDescription>}

        {children}

        {onDismiss && (
          <DialogPrimitive.Close className={mtc(closeStyles())} asChild>
            <Button
              aria-label="Dismiss banner"
              className={mtc(dismissStyles(), dismissClassName)}
              iconRight={solid('close')}
              onClick={onDismiss}
              variant="discreet"
              size="square"
            />
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
)

export const DialogFooter = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div className={mtc(footerStyles(), className)} {...props} />
)

export const DialogTitle = forwardRef<
  ElementRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & {
    center?: boolean
  }
>(({ center, className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={mtc(titleStyles({ center }), className)}
    {...props}
  />
))

export const DialogDescription = forwardRef<
  ElementRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description> & {
    center?: boolean
  }
>(({ center, className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={mtc(descriptionStyles({ center }), className)}
    {...props}
  />
))

DialogOverlay.displayName = DialogPrimitive.Overlay.displayName
DialogContent.displayName = DialogPrimitive.Content.displayName
DialogFooter.displayName = 'DialogFooter'
DialogTitle.displayName = DialogPrimitive.Title.displayName
DialogDescription.displayName = DialogPrimitive.Description.displayName

export const Dialog = Object.assign(DialogRoot, {
  Trigger: DialogPrimitive.Trigger,
  Portal: DialogPrimitive.Portal,
  Close: DialogPrimitive.Close,
  Overlay: DialogOverlay,
  Content: DialogContent,
  Footer: DialogFooter,
  Title: DialogTitle,
  Description: DialogDescription,
})
